'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

const benefits = [
  { title: 'Komisi Kompetitif', desc: 'Dapatkan komisi hingga 3% dari setiap transaksi yang berhasil', icon: 'CurrencyDollarIcon', color: 'bg-accent/10 text-accent' },
  { title: 'Dashboard Canggih', desc: 'Pantau lead, komisi, dan performa Anda secara real-time', icon: 'ChartBarIcon', color: 'bg-primary/10 text-primary' },
  { title: 'Link Referral Unik', desc: 'Bagikan kavling dengan link personal yang melacak setiap lead', icon: 'LinkIcon', color: 'bg-secondary/10 text-secondary' },
  { title: 'Materi Marketing', desc: 'Akses foto, video, dan materi promosi profesional siap pakai', icon: 'PhotoIcon', color: 'bg-blue/10 text-blue' },
  { title: 'Pelatihan Gratis', desc: 'Training produk dan teknik penjualan dari tim BETALAND.ID', icon: 'AcademicCapIcon', color: 'bg-purple/10 text-purple' },
  { title: 'Support 24/7', desc: 'Tim support siap membantu Anda kapan saja dibutuhkan', icon: 'ChatBubbleLeftRightIcon', color: 'bg-orange/10 text-orange' },
];

const steps = [
  { step: 1, title: 'Daftar', desc: 'Isi formulir pendaftaran dengan data diri lengkap' },
  { step: 2, title: 'Verifikasi', desc: 'Tim kami akan memverifikasi data dalam 1x24 jam' },
  { step: 3, title: 'Dapatkan Akun', desc: 'Akses dashboard partner dengan kode referral unik Anda' },
  { step: 4, title: 'Bagikan Kavling', desc: 'Bagikan link kavling ke calon pembeli potensial' },
  { step: 5, title: 'Dapatkan Lead', desc: 'Setiap calon pembeli yang masuk tercatat otomatis' },
  { step: 6, title: 'Dapatkan Komisi', desc: 'Komisi dibayarkan setelah transaksi selesai' },
];

const faqs = [
  { q: 'Apakah ada biaya untuk menjadi partner?', a: 'Tidak ada biaya pendaftaran. Bergabung sepenuhnya gratis.' },
  { q: 'Berapa besar komisi yang didapatkan?', a: 'Komisi bervariasi antara 1.5% - 3% tergantung proyek dan skema pembayaran.' },
  { q: 'Apakah perlu pengalaman di properti?', a: 'Tidak perlu. Kami menyediakan pelatihan lengkap untuk semua partner baru.' },
  { q: 'Bagaimana cara kerja referral tracking?', a: 'Setiap partner mendapat link unik. Ketika calon pembeli mengklik link Anda dan mengisi form, lead otomatis teratribusi ke akun Anda.' },
];

export default function JadiPartnerPage() {
  const [form, setForm] = useState({ nama: '', whatsapp: '', email: '', kota: '', pengalaman: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const supabase = createClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { error: dbError } = await supabase.from('partner_registrations').insert({
        full_name: form.nama.trim(),
        email: form.email.trim() || null,
        phone: form.whatsapp.trim(),
        city: form.kota.trim() || null,
        experience: form.pengalaman || null,
        status: 'pending',
      });
      if (dbError) throw dbError;
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.message || 'Gagal mengirim pendaftaran. Silakan coba lagi.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* HERO */}
      <section className="bg-gradient-to-br from-primary via-primary-dark to-[#021f18] py-20">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent/20 border border-accent/30 rounded-full text-accent text-xs font-semibold mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            Program Partner BETALAND.ID
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Hasilkan Komisi dengan<br />
            <span className="text-accent">Berbagi Kavling</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">
            Dapatkan penghasilan tambahan dengan membantu mempertemukan calon pembeli dengan kavling BETALAND.ID. Tidak perlu pengalaman, tidak ada biaya.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#daftar" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-accent-foreground font-bold rounded-xl hover:bg-accent/90 transition-colors shadow-lg">
              <Icon name="UserPlusIcon" size={20} /> Daftar Jadi Partner
            </a>
            <a href="https://wa.me/6281282760110?text=Halo%20BETALAND.ID%2C%20saya%20ingin%20informasi%20program%20partner." target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors border border-white/20">
              <Icon name="ChatBubbleLeftRightIcon" size={20} /> Tanya via WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-card border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {[
              { label: 'Partner Aktif', value: '200+' },
              { label: 'Total Komisi Dibayar', value: 'Rp 2.5 M+' },
              { label: 'Transaksi via Partner', value: '150+' },
              { label: 'Komisi Tertinggi', value: 'Rp 25 Jt' },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-2xl font-bold text-primary">{s.value}</p>
                <p className="text-sm text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-16">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Keuntungan Partner</p>
          <h2 className="text-3xl font-bold text-foreground">Mengapa Bergabung dengan BETALAND.ID?</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((b) => (
            <div key={b.title} className="bg-card rounded-2xl p-6 border border-border shadow-card">
              <div className={`w-12 h-12 rounded-xl ${b.color} flex items-center justify-center mb-4`}>
                <Icon name={b.icon as any} size={24} />
              </div>
              <h3 className="font-bold text-foreground mb-2">{b.title}</h3>
              <p className="text-sm text-muted-foreground">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-muted/50 py-16">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Cara Kerja</p>
            <h2 className="text-3xl font-bold text-foreground">Mulai Hasilkan Komisi dalam 6 Langkah</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {steps.map((s) => (
              <div key={s.step} className="bg-card rounded-2xl p-6 border border-border shadow-card flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">{s.step}</div>
                <div>
                  <h3 className="font-bold text-foreground mb-1">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REGISTRATION FORM */}
      <section id="daftar" className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-16">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Pendaftaran</p>
            <h2 className="text-3xl font-bold text-foreground">Daftar Jadi Partner Sekarang</h2>
            <p className="text-muted-foreground mt-2">Gratis, tanpa biaya pendaftaran</p>
          </div>
          <div className="bg-card rounded-2xl p-8 border border-border shadow-card">
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                    <Icon name="ExclamationCircleIcon" size={16} className="flex-shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Nama Lengkap *</label>
                    <input type="text" required value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })}
                      placeholder="Nama sesuai KTP"
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Nomor WhatsApp *</label>
                    <input type="tel" required value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                      placeholder="08xxxxxxxxxx"
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="email@contoh.com"
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Kota Domisili *</label>
                  <input type="text" required value={form.kota} onChange={(e) => setForm({ ...form, kota: e.target.value })}
                    placeholder="Jakarta, Bogor, Depok, dll"
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Pengalaman di Properti</label>
                  <select value={form.pengalaman} onChange={(e) => setForm({ ...form, pengalaman: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary">
                    <option value="">Pilih pengalaman</option>
                    <option value="none">Belum ada pengalaman</option>
                    <option value="1-2">1-2 tahun</option>
                    <option value="3-5">3-5 tahun</option>
                    <option value="5+">Lebih dari 5 tahun</option>
                  </select>
                </div>
                <button type="submit" disabled={submitting} className="w-full py-4 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary-dark transition-colors text-base disabled:opacity-60 flex items-center justify-center gap-2">
                  {submitting ? <><Icon name="ArrowPathIcon" size={18} className="animate-spin" />Mengirim...</> : 'Daftar Jadi Partner BETALAND.ID'}
                </button>
                <p className="text-xs text-muted-foreground text-center">Dengan mendaftar, Anda menyetujui syarat dan ketentuan program partner BETALAND.ID</p>
              </form>
            ) : (
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                  <Icon name="CheckCircleIcon" size={40} className="text-primary" />
                </div>
                <h3 className="font-bold text-foreground text-xl mb-3">Pendaftaran Berhasil!</h3>
                <p className="text-muted-foreground mb-2">Terima kasih, <strong>{form.nama}</strong>!</p>
                <p className="text-muted-foreground mb-6">Tim BETALAND.ID akan menghubungi Anda dalam 1x24 jam untuk proses verifikasi dan mengirimkan detail akun partner Anda.</p>
                <a href="https://wa.me/6281282760110?text=Halo%20BETALAND.ID%2C%20saya%20baru%20mendaftar%20sebagai%20partner." target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white font-semibold rounded-xl hover:bg-[#1ebe5d] transition-colors">
                  <Icon name="ChatBubbleLeftRightIcon" size={18} /> Konfirmasi via WhatsApp
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/50 py-16">
        <div className="max-w-3xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-foreground">FAQ Partner</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-card rounded-xl border border-border overflow-hidden">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left font-semibold text-foreground hover:bg-muted/50 transition-colors">
                  <span>{faq.q}</span>
                  <Icon name={activeFaq === i ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={18} className="text-muted-foreground flex-shrink-0 ml-4" />
                </button>
                {activeFaq === i && <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOGIN PARTNER CTA */}
      <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-12">
        <div className="bg-card rounded-2xl p-8 border border-border text-center">
          <h3 className="font-bold text-foreground text-xl mb-2">Sudah Punya Akun Partner?</h3>
          <p className="text-muted-foreground mb-5">Masuk ke dashboard partner untuk melihat lead, komisi, dan performa Anda.</p>
          <Link href="/login" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary-dark transition-colors">
            <Icon name="ArrowRightOnRectangleIcon" size={18} /> Masuk sebagai Partner
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
